import { useIntl } from 'react-intl'

import { IconButton } from '@gto/ui/icon-button'
import { Menu } from '@gto/ui/menu'

import { useAccountPartialUpdate } from '@/api/gen'
import {
  BrazilFlagIcon,
  ChinaFlagIcon,
  CroatiaFlagIcon,
  FranceFlagIcon,
  GermanyFlagIcon,
  HungaryFlagIcon,
  ItalyFlagIcon,
  MexicoFlagIcon,
  NetherlandsFlagIcon,
  PolandFlagIcon,
  RomaniaFlagIcon,
  RussiaFlagIcon,
  SloveniaFlagIcon,
  SouthKoreaFlagIcon,
  SwedenFlagIcon,
  TaiwanFlagIcon,
  ThailandFlagIcon,
  TurkeyFlagIcon,
  UkraineFlagIcon,
  UnitedKingdomFlagIcon,
} from '@/assets/icons'
import { useAuth } from '@/hooks/use-auth'
import type { TLang } from '@/localise'
import { zIndex } from '@/utils/constants'

const languages: TLang[] = [
  { code: 'en', name: 'English', Icon: UnitedKingdomFlagIcon },
  { code: 'uk', name: 'Ukrainian', Icon: UkraineFlagIcon },
  { code: 'zh-tw', name: '繁體中文', Icon: TaiwanFlagIcon },
  { code: 'zh-cn', name: '中文', Icon: ChinaFlagIcon },
  { code: 'pt', name: 'Português', Icon: BrazilFlagIcon },
  { code: 'es', name: 'Español', Icon: MexicoFlagIcon },
  { code: 'fr', name: 'Français', Icon: FranceFlagIcon },
  { code: 'it', name: 'Italiano', Icon: ItalyFlagIcon },
  { code: 'th', name: 'ไทย', Icon: ThailandFlagIcon },
  { code: 'ro', name: 'Română', Icon: RomaniaFlagIcon },
  { code: 'pl', name: 'Polski', Icon: PolandFlagIcon },
  { code: 'hu', name: 'Magyar', Icon: HungaryFlagIcon },
  { code: 'ko', name: '한국어', Icon: SouthKoreaFlagIcon },
  { code: 'de', name: 'Deutsch', Icon: GermanyFlagIcon },
  { code: 'sl', name: 'Slovenščina', Icon: SloveniaFlagIcon },
  { code: 'nl', name: 'Nederlands', Icon: NetherlandsFlagIcon },
  { code: 'hr', name: 'Hrvatski', Icon: CroatiaFlagIcon },
  { code: 'ru', name: 'Russian', Icon: RussiaFlagIcon },
  { code: 'sv', name: 'Svenska', Icon: SwedenFlagIcon },
  { code: 'tr', name: 'Türkçe', Icon: TurkeyFlagIcon },
]

export const LangSelector = () => {
  const { isAuthorized } = useAuth()
  const { locale } = useIntl()

  const { mutate: updateAccount } = useAccountPartialUpdate({
    mutation: {
      onSuccess: (data) => {
        const lang = data.preferred_language
        if (lang != null) {
          const event = new CustomEvent('languageChange', { detail: { lang } })
          window.dispatchEvent(event)
        }
      },
      onError: (error) => {
        console.error('Error updating language:', error)
      },
    },
  })

  const handleLanguageChange = (lang: string) => {
    if (isAuthorized) {
      updateAccount({ data: { preferred_language: lang } })
    } else {
      const event = new CustomEvent('languageChange', { detail: { lang } })
      window.dispatchEvent(event)
    }
  }

  const currentLang = languages.find((lang) => lang.code === locale) ?? languages[0]

  return (
    <Menu.Root>
      <Menu.Trigger
        render={<IconButton>{currentLang?.Icon ? <currentLang.Icon /> : null}</IconButton>}
      />
      <Menu.Portal>
        {/* rbwizard's header sits at zIndex.header (13002), well above the
            design-system popover layer — keep the menu above the navbar. */}
        <Menu.Positioner
          align="end"
          style={{ zIndex: zIndex.header + 1 }}
        >
          <Menu.Popup className="max-h-[60vh] overflow-auto">
            <Menu.Group>
              {languages.map(({ code, Icon, name }) => (
                <Menu.Item
                  key={code}
                  aria-current={code === locale ? 'true' : undefined}
                  className="aria-[current=true]:text-primary-main"
                  onClick={() => handleLanguageChange(code)}
                >
                  <div className="flex size-5 items-center justify-center">
                    <Icon />
                  </div>
                  {name}
                </Menu.Item>
              ))}
            </Menu.Group>
          </Menu.Popup>
        </Menu.Positioner>
      </Menu.Portal>
    </Menu.Root>
  )
}
